import { useState, useEffect, useRef } from 'react';
import api, { STATUS_LABELS, SOURCE_LABELS, SOURCE_COLOR, STATUS_COLOR, formatDate } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { toast } from 'sonner';
import { undoableAction } from '../lib/undoable';
import OfferTemplateBar from './OfferTemplateBar';
import { de } from 'date-fns/locale';
import { X, Check, AlertTriangle, Clock, UserCheck, UserX, Trash2, CalendarIcon, Sparkles, Star, Users, MessageCircle, Mic, Square, Loader2, Pencil, Wine, Gift, FileText, Plus, Send, Ban } from 'lucide-react';

// Schalter für den Sprach-/Mikrofon-Button im "Reservierung anlegen"-Dialog.
// Auf `false` ist der Button ausgeblendet (das Backend + die Aufnahme-Logik bleiben
// vollständig erhalten). Zum Wieder-Aktivieren einfach auf `true` setzen.
const VOICE_INPUT_ENABLED = false;


// Helpers to convert between YYYY-MM-DD string and Date
function strToDate(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function dateToStr(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function buildTimeOptions(stepMin = 15, startHour = 0, endHour = 24) {
  const out = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += stepMin) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
}
const TIME_OPTIONS = buildTimeOptions(15);

// ----- Opening-rules helpers (mirror backend availability logic) -----

function hmToMin(s) {
  if (!s) return null;
  const [h, m] = s.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
function minToHm(min) {
  const h = String(Math.floor(min / 60)).padStart(2, '0');
  const m = String(min % 60).padStart(2, '0');
  return `${h}:${m}`;
}
function weekdayFromDateStr(dateStr) {
  if (!dateStr) return -1;
  const [y, m, d] = dateStr.split('-').map(Number);
  // JS getDay(): 0=Sun..6=Sat. Convert to 0=Mon..6=Sun.
  const js = new Date(y, m - 1, d).getDay();
  return (js + 6) % 7;
}

/** Returns array of [startMin, endMin] blocks for given date, [] if closed. */
// Resolve season ('summer'/'winter') for a date from settings, or null when off.
export function seasonForDate(dateStr, settings) {
  if (!settings || !settings.seasonal_hours_enabled) return null;
  const ov = settings.season_override || 'auto';
  if (ov === 'summer' || ov === 'winter') return ov;
  const md = (dateStr || '').slice(5, 10);
  if (md.length !== 5) return 'summer';
  const ss = settings.summer_start || '04-01';
  const se = settings.summer_end || '10-31';
  if (ss <= se) return (md >= ss && md <= se) ? 'summer' : 'winter';
  return (md >= ss || md <= se) ? 'summer' : 'winter';
}

// Pick the opening-hours row for a weekday, honoring the resolved season.
// Falls back to the default (season-less) row when the season is not customized.
function pickOpeningRow(openingHours, wd, season) {
  const rows = (openingHours || []).filter(o => o.weekday === wd);
  if (season) {
    const s = rows.find(r => (r.season || null) === season);
    if (s) return s;
  }
  return rows.find(r => !r.season) || rows[0] || null;
}

function getOpeningBlocksForDate(dateStr, openingHours, specialDays, settings) {
  if (!dateStr) return [];
  const sd = (specialDays || []).find(s => s.date === dateStr);
  if (sd) {
    if (sd.type === 'closed') return [];
    if (sd.type === 'special_open') {
      // Service-Wellen haben Vorrang: Fenster = erste Ankunft bis festes Wellen-Ende.
      const waves = sd.waves || [];
      if (waves.length > 0) {
        return waves
          .map(w => [hmToMin(w.first_arrival), hmToMin(w.end_time)])
          .filter(([a, b]) => a != null && b != null);
      }
      return (sd.blocks || [])
        .map(b => [hmToMin(b.start_time), hmToMin(b.end_time)])
        .filter(([a, b]) => a != null && b != null);
    }
  }
  const wd = weekdayFromDateStr(dateStr);
  const row = pickOpeningRow(openingHours, wd, seasonForDate(dateStr, settings));
  if (!row || row.closed) return [];
  return (row.blocks || []).map(b => [hmToMin(b.start_time), hmToMin(b.end_time)]).filter(([a, b]) => a != null && b != null);
}

function isDateClosed(dateStr, openingHours, specialDays, settings) {
  return getOpeningBlocksForDate(dateStr, openingHours, specialDays, settings).length === 0;
}

/** Returns the fixed wave end time (HH:MM) for a given start time on a wave day, or null. */
export function waveEndForDateTime(dateStr, startTime, specialDays) {
  const sd = (specialDays || []).find(s => s.date === dateStr);
  if (!sd || sd.type !== 'special_open' || !(sd.waves || []).length) return null;
  const m = hmToMin(startTime);
  if (m == null) return null;
  for (const w of sd.waves) {
    const first = hmToMin(w.first_arrival);
    const last = hmToMin(w.last_arrival);
    const end = hmToMin(w.end_time);
    if (first == null || end == null) continue;
    const lastArr = last != null ? last : end;
    if (m >= first && m <= lastArr && end > m) return w.end_time;
  }
  return null;
}

/** Empfohlene Standard-Aufenthaltsdauer (Min.) je Personenzahl – spiegelt die
 * Backend-Logik get_duration_for_persons (duration_rules → default_duration_minutes). */
export function durationForPersons(persons, settings) {
  const rules = (settings && settings.duration_rules) || [];
  for (const r of rules) {
    if (r.min_persons <= persons && persons <= r.max_persons) return r.duration_minutes;
  }
  return (settings && settings.default_duration_minutes) || 120;
}


/** Max. Dauer (Min.) von startTime bis Ende des Öffnungsblocks (Schließzeit), oder null wenn außerhalb. */
export function maxDurationForDateTime(dateStr, startTime, openingHours, specialDays, settings) {
  const m = hmToMin(startTime);
  if (m == null) return null;
  const blocks = getOpeningBlocksForDate(dateStr, openingHours, specialDays, settings);
  for (const [start, end] of blocks) {
    if (m >= start && m < end) return end - m;
  }
  return null;
}

export function validTimeOptionsForDate(dateStr, openingHours, specialDays, stepMin = 15, settings) {
  // Service-Wellen: nur die Ankunftszeiten je Welle anbieten.
  const sd = (specialDays || []).find(s => s.date === dateStr);
  if (sd && sd.type === 'special_open' && (sd.waves || []).length > 0) {
    const out = [];
    for (const w of sd.waves) {
      const first = hmToMin(w.first_arrival);
      const last = hmToMin(w.last_arrival);
      if (first == null || last == null) continue;
      const step = parseInt(w.interval_minutes) || stepMin;
      for (let m = first; m <= last; m += step) out.push(minToHm(m));
    }
    return Array.from(new Set(out)).sort();
  }
  const blocks = getOpeningBlocksForDate(dateStr, openingHours, specialDays, settings);
  if (blocks.length === 0) return [];
  const out = [];
  for (const [start, end] of blocks) {
    // Allow starts strictly before closing time (no point starting at closing minute)
    for (let m = start; m < end; m += stepMin) {
      out.push(minToHm(m));
    }
  }
  return Array.from(new Set(out)).sort();
}

function DatePickerField({ value, onChange, testId, openingHours, specialDays, disablePast = true, settings }) {
  const [open, setOpen] = useState(false);
  const date = strToDate(value);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isDayDisabled = (d) => {
    const ds = dateToStr(d);
    if (disablePast && d < today) return true;
    if (!openingHours && !specialDays) return false;
    return isDateClosed(ds, openingHours, specialDays, settings);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white hover:border-[#1C1C19] transition"
        >
          <span className={value ? 'text-[#1C1C19]' : 'text-[#9CA3AF]'}>
            {value ? date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Datum wählen'}
          </span>
          <CalendarIcon className="w-4 h-4 text-[#5C5C59]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => { if (d) { onChange(dateToStr(d)); setOpen(false); } }}
          disabled={isDayDisabled}
          locale={de}
          weekStartsOn={1}
          initialFocus
        />
        <div className="px-3 py-2 border-t border-[#E5E5E0] text-[10px] text-[#5C5C59] bg-[#F9F9F7]">
          Geschlossene Tage sind ausgegraut.
        </div>
      </PopoverContent>
    </Popover>
  );
}

function isTodayStr(s) {
  if (!s) return false;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return s === today;
}

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function TimePickerField({ value, onChange, testId, dateStr, openingHours, specialDays, availableTimes, hidePast = false, settings }) {
  // Tick once a minute so already-passed slots auto-disappear without manual refresh
  // (only when `hidePast` is explicitly enabled by the caller — admin defaults to off
  // so walk-ins and back-dated entries work even after opening hours have started).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hidePast || !isTodayStr(dateStr)) return;
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, [hidePast, dateStr]);

  let opts = openingHours
    ? validTimeOptionsForDate(dateStr, openingHours, specialDays, 15, settings)
    : TIME_OPTIONS;
  // Filter past times when the date is today (keep current value to avoid losing existing selection)
  if (hidePast && isTodayStr(dateStr)) {
    const nm = nowMinutes();
    opts = opts.filter(t => hmToMin(t) >= nm - 5 || t === value);
  }
  const hasOpening = opts.length > 0;
  // If the current value is no longer valid for this date, mark it
  const invalidValue = value && hasOpening && !opts.includes(value);
  const isFull = (t) => availableTimes && !availableTimes.has(t);
  return (
    <Select value={value || ''} onValueChange={onChange} disabled={!hasOpening && !!openingHours}>
      <SelectTrigger data-testid={testId} className={`font-mono ${invalidValue ? 'border-[#EF4444]' : ''}`}>
        <Clock className="w-4 h-4 text-[#5C5C59] mr-1" />
        <SelectValue placeholder={!hasOpening && openingHours ? (isTodayStr(dateStr) ? 'Heute keine Zeit mehr' : 'Geschlossen') : 'Uhrzeit'} />
      </SelectTrigger>
      <SelectContent className="max-h-[280px]">
        {opts.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[#5C5C59] text-center">
            {isTodayStr(dateStr) ? 'Heute sind alle Uhrzeiten bereits vergangen.' : 'Restaurant ist an diesem Tag geschlossen.'}
          </div>
        ) : opts.map(t => {
          const full = isFull(t);
          return (
            <SelectItem
              key={t}
              value={t}
              className={`font-mono ${full ? 'text-[#9CA3AF]' : ''}`}
              data-testid={`${testId}-opt-${t}${full ? '-full' : ''}`}
            >
              <span className="flex items-center gap-2">
                <span>{t}</span>
                {full && <span className="text-[#EF4444] text-[10px]">⛔ voll</span>}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export function ReservationDialog({ open, onClose, reservation, tables, areas, onUpdate, onAddWalkInForTable, onAddReservationForTable }) {
  const [edit, setEdit] = useState({});
  const [editing, setEditing] = useState(false);
  const [openingHours, setOpeningHours] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [availableTimes, setAvailableTimes] = useState(null);
  const [tagsCatalog, setTagsCatalog] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [dayReservations, setDayReservations] = useState([]);
  const [pickedTableIds, setPickedTableIds] = useState([]);
  const [restaurantName, setRestaurantName] = useState('La Contadina');
  const [settings, setSettings] = useState(null);
  const [guestHistory, setGuestHistory] = useState(null);
  const [guestVouchers, setGuestVouchers] = useState([]);
  const [offerOpen, setOfferOpen] = useState(false);
  const [localOffer, setLocalOffer] = useState(null);

  // Gutschein-Erkennung: aktive Gutscheine des Gasts per E-Mail laden
  useEffect(() => {
    if (!open || !reservation || !reservation.email) { setGuestVouchers([]); return; }
    let cancelled = false;
    api.get(`/vouchers/by-email?email=${encodeURIComponent(reservation.email)}`)
      .then(r => { if (!cancelled) setGuestVouchers((r.data?.vouchers || []).filter(v => v.status === 'active')); })
      .catch(() => { if (!cancelled) setGuestVouchers([]); });
    return () => { cancelled = true; };
  }, [open, reservation]);

  // Stammgast-Erkennung: Besuchshistorie des Gasts laden (Match per Telefon/Name)
  useEffect(() => {
    if (!open || !reservation) { setGuestHistory(null); return; }
    let cancelled = false;
    const params = new URLSearchParams({
      phone: reservation.phone || '',
      name: reservation.customer_name || '',
      exclude_id: reservation.id || '',
    });
    api.get(`/guests/history?${params.toString()}`)
      .then(r => { if (!cancelled) setGuestHistory(r.data); })
      .catch(() => { if (!cancelled) setGuestHistory(null); });
    return () => { cancelled = true; };
  }, [open, reservation]);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get('/opening-hours/all'), api.get('/special-days'), api.get('/tags/public'), api.get('/table-combinations/all')])
      .then(([a, b, c, d]) => { setOpeningHours(a.data); setSpecialDays(b.data); setTagsCatalog(c.data || []); setCombinations(d.data || []); })
      .catch(() => {});
    api.get('/settings/public').then(r => { setSettings(r.data || null); if (r.data?.restaurant_name) setRestaurantName(r.data.restaurant_name); }).catch(() => {});
  }, [open]);

  // Load reservations for the edited date so the mini-plan can show live availability
  useEffect(() => {
    if (!editing || !edit.date) { setDayReservations([]); return; }
    let cancelled = false;
    api.get(`/reservations?date=${edit.date}`)
      .then(r => { if (!cancelled) setDayReservations(r.data || []); })
      .catch(() => setDayReservations([]));
    return () => { cancelled = true; };
  }, [editing, edit.date]);

  // Fetch available slots whenever date/persons change in edit mode
  useEffect(() => {
    if (!editing || !edit.date || !edit.persons) { setAvailableTimes(null); return; }
    let cancelled = false;
    api.post('/availability', { date: edit.date, persons: edit.persons })
      .then(r => {
        if (cancelled) return;
        const set = new Set((r.data?.slots || []).map(s => s.time));
        // Keep current reservation's time as available (admin is editing their own slot)
        if (reservation?.start_time && reservation?.date === edit.date) set.add(reservation.start_time);
        setAvailableTimes(set);
      })
      .catch(() => setAvailableTimes(null));
    return () => { cancelled = true; };
  }, [editing, edit.date, edit.persons, reservation]);

  useEffect(() => {
    if (reservation) {
      setEdit({
        customer_name: reservation.customer_name,
        phone: reservation.phone,
        email: reservation.email,
        date: reservation.date,
        start_time: reservation.start_time,
        end_time: reservation.end_time,
        persons: reservation.persons,
        assigned_table_id: reservation.assigned_table_id,
        assigned_combination_id: reservation.assigned_combination_id,
        note: reservation.note,
        internal_note: reservation.internal_note,
      });
      setLocalOffer(reservation.offer || null);
      // Seed picked tables from current assignment
      if (reservation.assigned_combination_id) {
        // resolved later when combinations are loaded
        setPickedTableIds([]);
      } else if (reservation.assigned_table_id) {
        setPickedTableIds([reservation.assigned_table_id]);
      } else {
        setPickedTableIds([]);
      }
      setEditing(false);
    }
  }, [reservation]);

  // When combinations load and we have a combo assignment, resolve member tables
  useEffect(() => {
    if (!reservation?.assigned_combination_id || !combinations.length) return;
    const c = combinations.find(c => c.id === reservation.assigned_combination_id);
    if (c) setPickedTableIds(c.table_ids || []);
  }, [reservation, combinations]);

  if (!reservation) return null;

  const changeStatus = async (status) => {
    const prevStatus = reservation.status;
    try {
      await api.post(`/reservations/${reservation.id}/status`, { status });
      undoableAction({
        label: `Status: ${STATUS_LABELS[status]}`,
        description: `${reservation.customer_name} · zurück zu „${STATUS_LABELS[prevStatus] || prevStatus}"?`,
        onUndo: () => api.post(`/reservations/${reservation.id}/status`, { status: prevStatus }),
        onUndone: onUpdate,
      });
      onUpdate();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const releaseTable = async () => {
    const prevEnd = reservation.end_time;
    const prevStatus = reservation.status;
    try {
      await api.post(`/reservations/${reservation.id}/release-table`);
      undoableAction({
        label: 'Tisch freigegeben',
        description: `${reservation.customer_name} · Ende zurück auf ${prevEnd}?`,
        onUndo: () => api.put(`/reservations/${reservation.id}`, { end_time: prevEnd, status: prevStatus }),
        onUndone: onUpdate,
      });
      onUpdate();
      onClose();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  const saveEdit = async () => {
    try {
      const payload = { ...edit };
      // Mirror picker state into payload
      if (pickedTableIds.length >= 2) {
        payload.table_ids = pickedTableIds;
        payload.assigned_table_id = null;
      } else if (pickedTableIds.length === 1) {
        payload.table_ids = [pickedTableIds[0]];
        payload.assigned_table_id = pickedTableIds[0];
        payload.assigned_combination_id = null;
      } else {
        payload.table_ids = [];
      }
      await api.put(`/reservations/${reservation.id}`, payload);
      toast.success('Gespeichert');
      onUpdate();
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const deleteReservation = async () => {
    if (!window.confirm('Reservierung wirklich löschen?')) return;
    try {
      await api.delete(`/reservations/${reservation.id}`);
      toast.success('Gelöscht');
      onUpdate();
      onClose();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  // Split a combination reservation down to one kept table; backend frees the rest
  // and re-checks availability (excluding this reservation).
  const reduceCombination = async (keepTableId) => {
    const keepNum = tables.find(t => t.id === keepTableId)?.table_number;
    if (!window.confirm(`Kombination auflösen und nur Tisch ${keepNum} behalten? Die übrigen Tische werden freigegeben.`)) return;
    try {
      await api.put(`/reservations/${reservation.id}`, { assigned_table_id: keepTableId });
      toast.success(`Kombination aufgelöst – nur Tisch ${keepNum} behalten, übrige frei.`);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tisch konnte nicht freigegeben werden (Konflikt).');
    }
  };

  const status = reservation.status;

  const sendWhatsApp = () => {
    const r = reservation;
    const digits = (r.phone || '').replace(/[^\d]/g, '');
    if (!digits) {
      toast.info('Keine Telefonnummer hinterlegt – bitte zuerst eine Telefonnummer eintragen.');
      return;
    }
    const num = digits.replace(/^00/, '').replace(/^0/, '49');
    const area = areas.find(a => a.id === r.area_id);
    let dateFmt = r.date;
    try {
      dateFmt = new Date(r.date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { /* keep raw */ }
    const toMin = (hm) => { const [h, m] = (hm || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
    let durStr = '';
    if (r.start_time && r.end_time) {
      let d = toMin(r.end_time) - toMin(r.start_time);
      if (d < 0) d += 1440;
      const h = Math.floor(d / 60), m = d % 60;
      durStr = ` (Aufenthalt ca. ${h > 0 ? `${h} Std ` : ''}${m > 0 ? `${m} Min` : ''})`.replace(/\s+\)$/, ')');
    }
    const offers = Array.isArray(r.selected_offers) ? r.selected_offers : [];
    const offerLine = offers.length ? `\n🍷 Vorbestellung: ${offers.map(o => `${o.quantity}× ${o.name}`).join(', ')}` : '';
    const msg = `Hallo ${r.customer_name},\n\nIhre Reservierung im ${restaurantName} ist bestätigt:\n📅 ${dateFmt}\n🕖 ${r.start_time}–${r.end_time} Uhr${durStr}\n👥 ${r.persons} Personen${area ? ` · Bereich: ${area.name}` : ''}${offerLine}\n\nWir freuen uns auf Sie!`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reservation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full" style={{ background: SOURCE_COLOR[reservation.source] }}></span>
            <span className="font-display">{reservation.customer_name}</span>
            <span className="text-xs uppercase tracking-wider px-2 py-1 rounded font-mono" style={{ background: STATUS_COLOR[status] + '22', color: STATUS_COLOR[status] }}>
              {STATUS_LABELS[status]}
            </span>
          </DialogTitle>
        </DialogHeader>

        {guestHistory && (guestHistory.visits > 0 || guestHistory.no_shows > 0) && (
          <div className="flex flex-wrap items-center gap-2" data-testid="guest-history">
            {guestHistory.is_regular && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" data-testid="guest-regular-badge">
                <Star className="w-3.5 h-3.5 fill-current" /> Stammgast
              </span>
            )}
            {guestHistory.visits > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#F1F5F9] text-[#334155] border border-[#E2E8F0]" data-testid="guest-visits">
                <UserCheck className="w-3.5 h-3.5" /> {guestHistory.visits} {guestHistory.visits === 1 ? 'Besuch' : 'Besuche'}
              </span>
            )}
            {guestHistory.last_visit && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#F1F5F9] text-[#334155] border border-[#E2E8F0]" data-testid="guest-last-visit">
                <Clock className="w-3.5 h-3.5" /> Zuletzt {formatDate(guestHistory.last_visit)}
              </span>
            )}
            {guestHistory.no_shows > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]" data-testid="guest-noshows">
                <AlertTriangle className="w-3.5 h-3.5" /> {guestHistory.no_shows}× No-Show
              </span>
            )}
          </div>
        )}

        {guestVouchers.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FEFCE8] border border-[#FDE68A]" data-testid="guest-voucher-hint">
            <Gift className="w-4 h-4 text-[#CA8A04] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-[#854D0E]">
                {guestVouchers.length === 1 ? 'Aktiver Gutschein' : `${guestVouchers.length} aktive Gutscheine`} vorhanden
              </span>
              <div className="text-xs text-[#92400E] mt-0.5 space-y-0.5">
                {guestVouchers.map(v => (
                  <div key={v.id} data-testid={`guest-voucher-${v.code}`}>
                    <span className="font-mono">{v.code}</span> · Restwert {Number(v.remaining || 0).toFixed(2)} €
                    {v.valid_until ? ` · gültig bis ${formatDate(v.valid_until)}` : ''}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {localOffer && (localOffer.items || []).length > 0 && (
          <button
            type="button"
            onClick={() => setOfferOpen(true)}
            className={`w-full flex items-center justify-between gap-2 p-3 rounded-lg border text-left transition ${localOffer.status === 'accepted' ? 'bg-[#F0FDF4] border-[#BBF7D0] hover:bg-[#DCFCE7]' : localOffer.status === 'sent' ? 'bg-[#E0F2FE] border-[#BAE6FD] hover:bg-[#E0F2FE]' : 'bg-[#F8FAFC] border-[#E2E8F0] hover:bg-[#F1F5F9]'}`}
            data-testid="offer-status-badge"
          >
            <span className="flex items-center gap-2 text-sm">
              <FileText className={`w-4 h-4 ${localOffer.status === 'accepted' ? 'text-[#16A34A]' : localOffer.status === 'sent' ? 'text-[#0369A1]' : 'text-[#64748B]'}`} />
              <span className="font-medium text-[#1C1C19]">
                {localOffer.status === 'accepted' ? 'Angebot angenommen' : localOffer.status === 'sent' ? 'Angebot gesendet' : 'Angebot-Entwurf'}
              </span>
              <span className="text-[#64748B]">· {Number(localOffer.total || 0).toFixed(2).replace('.', ',')} €</span>
            </span>
            <span className="text-xs text-[#64748B]">Bearbeiten</span>
          </button>
        )}
        {guestHistory?.can_edit_favorite && (
          <div className="flex items-center gap-2 -mt-1" data-testid="guest-favorite-row">
            <FavoriteTableEditor
              phone={reservation.phone}
              tables={tables}
              areas={areas}
              favoriteTableId={guestHistory.favorite_table_id}
              favoriteTableNumber={guestHistory.favorite_table_number}
              favoriteSource={guestHistory.favorite_table_source}
              onChanged={(d) => setGuestHistory(g => ({
                ...g,
                favorite_table_id: d.favorite_table_id,
                favorite_table_number: d.favorite_table_number,
                favorite_table_source: d.favorite_table_source,
              }))}
            />
          </div>
        )}

        <div className="space-y-4">
          {!editing ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Datum" value={formatDate(reservation.date)} />
              <Field label="Uhrzeit" value={`${reservation.start_time} – ${reservation.end_time}`} mono />
              <Field label="Personen" value={reservation.persons} />
              <Field label="Quelle" value={SOURCE_LABELS[reservation.source]} />
              <Field label="Telefon" value={reservation.phone || '–'} />
              <Field label="E-Mail" value={reservation.email || '–'} />
              <Field label="Tisch" value={(() => {
                if (reservation.assigned_combination_id) {
                  const combo = combinations.find(c => c.id === reservation.assigned_combination_id);
                  if (combo) {
                    const nums = (combo.table_ids || [])
                      .map(tid => tables.find(t => t.id === tid)?.table_number)
                      .filter(Boolean)
                      .join(' + ');
                    return `${nums || '—'} · Gruppe „${combo.name}"`;
                  }
                  return 'Tischgruppe';
                }
                return tables.find(t => t.id === reservation.assigned_table_id)?.table_number || '–';
              })()} />
              <Field label="Bereich" value={areas.find(a => a.id === reservation.area_id)?.name || '–'} />
              <Field label="Reserviert am" value={formatCreatedAt(reservation.created_at)} mono />
              {reservation.assigned_combination_id && !editing && (() => {
                const combo = combinations.find(c => c.id === reservation.assigned_combination_id);
                const comboTables = (combo?.table_ids || []).map(tid => tables.find(t => t.id === tid)).filter(Boolean);
                if (comboTables.length < 2) return null;
                return (
                  <div className="col-span-2 bg-[#FFF7ED] border border-[#FED7AA] rounded p-3" data-testid="reduce-combo-block">
                    <p className="text-xs uppercase tracking-wider text-[#9A3412] mb-1">Kombination teilen</p>
                    <p className="text-xs text-[#9A3412]/80 mb-2">Nur 2 Gäste gekommen? Einen Tisch behalten – die übrigen werden sofort frei.</p>
                    <div className="flex flex-wrap gap-2">
                      {comboTables.map(t => (
                        <Button
                          key={t.id}
                          size="sm"
                          variant="outline"
                          onClick={() => reduceCombination(t.id)}
                          data-testid={`reduce-keep-${t.id}`}
                          className="border-[#F97316] text-[#9A3412] hover:bg-[#F97316] hover:text-white"
                        >
                          Nur Tisch {t.table_number} behalten
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {reservation.note && <Field label="Bemerkung Gast" value={reservation.note} fullWidth />}
              {reservation.internal_note && <Field label="Interne Notiz" value={reservation.internal_note} fullWidth />}
              {Array.isArray(reservation.tag_ids) && reservation.tag_ids.length > 0 && tagsCatalog.length > 0 && (
                <div className="col-span-2" data-testid="resv-tags-display">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">Wünsche / Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {reservation.tag_ids.map(tid => {
                      const t = tagsCatalog.find(x => x.id === tid);
                      if (!t) return null;
                      return (
                        <span
                          key={tid}
                          data-testid={`resv-tag-${tid}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                          style={{ background: (t.color || '#9CA3AF') + '22', color: t.color || '#1C1C19' }}
                        >
                          {t.icon && <span>{t.icon}</span>}
                          {t.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {Array.isArray(reservation.selected_offers) && reservation.selected_offers.length > 0 && (
                <div className="col-span-2" data-testid="resv-offers-display">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">Vorbestellung</p>
                  <div className="space-y-0.5">
                    {reservation.selected_offers.map((o, i) => (
                      <div key={o.offer_id || i} className="flex justify-between text-sm text-[#1C1C19]" data-testid={`resv-offer-${o.offer_id || i}`}>
                        <span><span className="font-medium">{o.quantity}×</span> {o.name}</span>
                        {Number(o.price) > 0 && <span className="text-[#5C5C59] font-mono">{((o.quantity || 1) * (Number(o.price) || 0)).toFixed(2).replace('.', ',')} €</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {reservation.is_short_slot && (
                <div className="col-span-2 bg-[#FFFBEB] border border-[#EAB308] rounded p-2 text-xs flex gap-2">
                  <Clock className="w-4 h-4 text-[#EAB308]" />
                  Kurzzeit-Reservierung
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={edit.customer_name || ''} onChange={e => setEdit({...edit, customer_name: e.target.value})} data-testid="edit-name" /></div>
              <div><Label>Telefon</Label><Input value={edit.phone || ''} onChange={e => setEdit({...edit, phone: e.target.value})} data-testid="edit-phone" /></div>
              <div><Label>E-Mail</Label><Input value={edit.email || ''} onChange={e => setEdit({...edit, email: e.target.value})} data-testid="edit-email" /></div>
              <div><Label>Datum</Label><DatePickerField value={edit.date || ''} onChange={(v) => setEdit({ ...edit, date: v })} testId="edit-date" openingHours={openingHours} specialDays={specialDays} disablePast={false} settings={settings} /></div>
              <div><Label>Personen</Label><Input type="number" min="1" value={edit.persons || 1} onChange={e => setEdit({...edit, persons: parseInt(e.target.value)})} data-testid="edit-persons" /></div>
              <div><Label>Beginn</Label><TimePickerField value={edit.start_time || ''} onChange={(v) => setEdit({ ...edit, start_time: v })} testId="edit-start" dateStr={edit.date} openingHours={openingHours} specialDays={specialDays} availableTimes={availableTimes} settings={settings} /></div>
              <div><Label>Ende</Label><TimePickerField value={edit.end_time || ''} onChange={(v) => setEdit({ ...edit, end_time: v })} testId="edit-end" dateStr={edit.date} openingHours={openingHours} specialDays={specialDays} settings={settings} /></div>
              <div className="col-span-2">
                <Label>Tisch (klicken zum Wählen · Mehrfach = Tischgruppe)</Label>
                <MiniFloorPlanPicker
                  tables={tables}
                  areas={areas}
                  combinations={combinations}
                  dayReservations={dayReservations}
                  startTime={edit.start_time}
                  durationMinutes={edit.start_time && edit.end_time ? (hmToMin(edit.end_time) - hmToMin(edit.start_time)) : 120}
                  persons={edit.persons}
                  pickedTableIds={pickedTableIds}
                  setPickedTableIds={setPickedTableIds}
                  excludeReservationId={reservation.id}
                  date={edit.date}
                />
              </div>
              <div className="col-span-2"><Label>Bemerkung Gast</Label><Textarea value={edit.note || ''} onChange={e => setEdit({...edit, note: e.target.value})} data-testid="edit-note" /></div>
              <div className="col-span-2"><Label>Interne Notiz</Label><Textarea value={edit.internal_note || ''} onChange={e => setEdit({...edit, internal_note: e.target.value})} data-testid="edit-internal" /></div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {!editing ? (
            <>
              {status === 'pending' && (
                <>
                  <Button onClick={() => changeStatus('confirmed')} data-testid="action-confirm" className="bg-[#22C55E] hover:bg-[#16A34A]">
                    <Check className="w-4 h-4 mr-1" /> Bestätigen
                  </Button>
                  <Button onClick={() => changeStatus('declined')} variant="outline" data-testid="action-decline">
                    <X className="w-4 h-4 mr-1" /> Ablehnen
                  </Button>
                </>
              )}
              {status === 'confirmed' && (
                <Button onClick={() => changeStatus('seated')} data-testid="action-seated" className="bg-[#F97316] hover:bg-[#EA580C]">
                  <UserCheck className="w-4 h-4 mr-1" /> Sitzt
                </Button>
              )}
              {status === 'arrived' && (
                <Button onClick={() => changeStatus('seated')} data-testid="action-seated" className="bg-[#F97316] hover:bg-[#EA580C]">
                  <UserCheck className="w-4 h-4 mr-1" /> Sitzt
                </Button>
              )}
              {(status === 'arrived' || status === 'seated') && (
                <Button onClick={releaseTable} variant="outline" data-testid="action-release">
                  Tisch freigeben
                </Button>
              )}
              {['confirmed', 'pending'].includes(status) && (
                <Button onClick={() => changeStatus('no_show')} variant="outline" data-testid="action-noshow">
                  <UserX className="w-4 h-4 mr-1" /> No-Show
                </Button>
              )}
              {!['cancelled', 'completed', 'declined', 'no_show'].includes(status) && (
                <Button onClick={() => changeStatus('cancelled')} variant="outline" data-testid="action-cancel">
                  Stornieren
                </Button>
              )}
              <Button onClick={() => setEditing(true)} variant="outline" data-testid="action-edit">Bearbeiten</Button>
              <Button
                onClick={() => setOfferOpen(true)}
                variant="outline"
                className="border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white"
                data-testid="action-offer"
              >
                <FileText className="w-4 h-4 mr-1" /> Angebot
              </Button>
              <Button
                onClick={sendWhatsApp}
                variant="outline"
                className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white"
                data-testid="action-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp-Bestätigung
              </Button>
              <Button onClick={deleteReservation} variant="outline" className="text-[#EF4444] border-[#EF4444]" data-testid="action-delete">
                <Trash2 className="w-4 h-4" />
              </Button>
              {onAddWalkInForTable && reservation.assigned_table_id && (
                <Button
                  onClick={() => onAddWalkInForTable(reservation.assigned_table_id)}
                  variant="outline"
                  className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
                  data-testid="action-add-walkin-table"
                >
                  <UserCheck className="w-4 h-4 mr-1" /> Walk-in für Tisch
                </Button>
              )}
              {onAddReservationForTable && reservation.assigned_table_id && (
                <Button
                  onClick={() => onAddReservationForTable(reservation.assigned_table_id)}
                  variant="outline"
                  data-testid="action-add-reservation-table"
                >
                  <Check className="w-4 h-4 mr-1" /> Zusatz-Reservierung
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={() => setEditing(false)} variant="outline" data-testid="edit-cancel-btn">Abbrechen</Button>
              <Button onClick={saveEdit} className="bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="edit-save-btn">Speichern</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <OfferDialog
        open={offerOpen}
        onClose={() => setOfferOpen(false)}
        reservation={reservation}
        offer={localOffer}
        onSaved={(data) => { if (data?.offer) setLocalOffer(data.offer); onUpdate && onUpdate(); }}
      />
    </Dialog>
  );
}

// Angebot erstellen & senden: freie Positionen (Beschreibung, Menge, Preis),
// Freitext, Gesamtbetrag, als CI-E-Mail an den Gast (Entwurf / gesendet).
export function OfferDialog({ open, onClose, reservation, offer, onSaved }) {
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: '' }]);
  const [note, setNote] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !reservation) return;
    const off = offer || reservation.offer;
    if (off && Array.isArray(off.items) && off.items.length) {
      setItems(off.items.map(i => ({
        description: i.description || '',
        quantity: i.quantity ?? 1,
        unit_price: i.unit_price ?? '',
      })));
      setNote(off.note || '');
    } else {
      setItems([{ description: '', quantity: 1, unit_price: '' }]);
      setNote('');
    }
    setEmail(reservation.email || '');
  }, [open, reservation, offer]);

  const total = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  const updateItem = (idx, field, val) => setItems(arr => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const addItem = () => setItems(arr => [...arr, { description: '', quantity: 1, unit_price: '' }]);
  const removeItem = (idx) => setItems(arr => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const submit = async (send) => {
    const cleanItems = items
      .filter(i => (i.description || '').trim())
      .map(i => ({ description: i.description.trim(), quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
    if (!cleanItems.length) { toast.error('Bitte mindestens eine Position mit Beschreibung angeben.'); return; }
    if (send && !(email || '').trim()) { toast.error('Bitte eine E-Mail-Adresse für den Versand angeben.'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/reservations/${reservation.id}/offer`, {
        items: cleanItems,
        note,
        email: (email || '').trim() || null,
        send,
      });
      toast.success(send ? 'Angebot gesendet' : 'Angebot als Entwurf gespeichert');
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileText className="w-5 h-5" /> Angebot · {reservation.customer_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <OfferTemplateBar items={items} note={note} onApply={(newItems, newNote) => { setItems(newItems); if (newNote) setNote(newNote); }} />
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Positionen</Label>
            <div className="space-y-2 mt-2" data-testid="offer-items">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-start gap-2" data-testid={`offer-item-${idx}`}>
                  <Input
                    value={it.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    placeholder="Beschreibung (z. B. 3-Gänge-Menü)"
                    className="flex-1"
                    data-testid={`offer-item-desc-${idx}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    placeholder="Menge"
                    className="w-16 text-center"
                    data-testid={`offer-item-qty-${idx}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={it.unit_price}
                    onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                    placeholder="€"
                    className="w-24 text-right"
                    data-testid={`offer-item-price-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(idx)}
                    className="text-[#EF4444] shrink-0"
                    data-testid={`offer-item-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              className="mt-2"
              data-testid="offer-add-item"
            >
              <Plus className="w-4 h-4 mr-1" /> Position hinzufügen
            </Button>
          </div>

          <div className="flex items-center justify-between border-t border-[#E5E5E0] pt-3">
            <span className="text-sm font-medium text-[#5C5C59]">Gesamt</span>
            <span className="text-lg font-semibold text-[#1C1C19]" data-testid="offer-total">
              {total.toFixed(2).replace('.', ',')} €
            </span>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Anschreiben / Hinweis (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Persönliche Nachricht, Getränkepauschale, Anzahlung, Raum-Info …"
              rows={3}
              className="mt-1"
              data-testid="offer-note"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">E-Mail des Gasts</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="gast@example.com"
              className="mt-1"
              data-testid="offer-email"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="offer-cancel">Abbrechen</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={busy} data-testid="offer-save-draft">
            Entwurf speichern
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={busy}
            className="bg-[#1C1C19] hover:bg-[#3A3A35]"
            data-testid="offer-send"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Angebot senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false, fullWidth = false }) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">{label}</p>
      <p className={`text-sm text-[#1C1C19] ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FavoriteTableEditor — set/edit a guest's favorite table manually (stored per
// phone number on the backend). Manual choice overrides the auto-detected one.
// Used in the reservation detail dialog and in the phone-match banner.
// ---------------------------------------------------------------------------
export function FavoriteTableEditor({ phone, tables, areas, favoriteTableId, favoriteTableNumber, favoriteSource, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const areaName = (id) => (areas || []).find(a => a.id === id)?.name || '';
  const sortedTables = (tables || [])
    .filter(t => t.active)
    .slice()
    .sort((a, b) => {
      const an = areaName(a.area_id), bn = areaName(b.area_id);
      if (an !== bn) return an.localeCompare(bn);
      return String(a.table_number).localeCompare(String(b.table_number), undefined, { numeric: true });
    });

  const save = async (val) => {
    const newId = val === '__auto__' ? null : val;
    setSaving(true);
    try {
      const r = await api.put('/guests/favorite-table', { phone, favorite_table_id: newId });
      toast.success(newId ? `Lieblingstisch: Tisch ${r.data.favorite_table_number}` : 'Lieblingstisch auf Automatik gesetzt');
      onChanged?.(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lieblingstisch konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="fav-table-editor">
        <Select value={favoriteTableId || '__auto__'} onValueChange={save} disabled={saving}>
          <SelectTrigger data-testid="fav-table-select" className="h-8 text-xs w-[220px]">
            <Star className="w-3.5 h-3.5 text-[#5C5C59] mr-1" />
            <SelectValue placeholder="Tisch wählen" />
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="__auto__" data-testid="fav-table-opt-auto">Automatik (kein fester Tisch)</SelectItem>
            {sortedTables.map(t => (
              <SelectItem key={t.id} value={t.id} data-testid={`fav-table-opt-${t.id}`}>
                Tisch {t.table_number}{areaName(t.area_id) ? ` · ${areaName(t.area_id)}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          className="text-xs text-[#5C5C59] hover:text-[#1C1C19] transition"
          data-testid="fav-table-cancel"
        >
          Abbrechen
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid="fav-table-edit-btn"
      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-[#FEF9C3] text-[#854D0E] border border-[#FDE68A] hover:bg-[#FEF08A] transition"
    >
      <Star className="w-3.5 h-3.5 fill-current" />
      {favoriteTableNumber != null
        ? <span>Lieblingstisch: Tisch {favoriteTableNumber}{favoriteSource === 'manual' ? ' (manuell)' : ''}</span>
        : <span>Lieblingstisch festlegen</span>}
      <Pencil className="w-3 h-3" />
    </button>
  );
}

// Formats a stored ISO timestamp (UTC) as a German date+time in Europe/Berlin
function formatCreatedAt(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '–';
  try {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Berlin',
    }).format(d) + ' Uhr';
  } catch {
    return d.toLocaleString('de-DE');
  }
}

export function NewReservationDialog({ open, onClose, tables, areas, onCreated, defaultDate, defaultTableId, walkIn = false, shareTable = false, defaultPhone = '', defaultName = '', defaultEmail = '', defaultPersons, defaultTime = '' }) {
  const [form, setForm] = useState({
    customer_name: walkIn ? 'Walk-in' : '',
    phone: '',
    email: '',
    date: defaultDate || '',
    start_time: '',
    persons: 2,
    duration_minutes: 120,
    assigned_table_id: defaultTableId || '',
    note: '',
    internal_note: '',
    source: walkIn ? 'walk_in' : 'manual',
    auto_confirm: true,
  });
  const [openingHours, setOpeningHours] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [availableTimes, setAvailableTimes] = useState(null);
  const [settings, setSettings] = useState(null);
  // Mini-floor-plan state
  const [pickedTableIds, setPickedTableIds] = useState([]);
  const [dayReservations, setDayReservations] = useState([]);
  const [allCombinations, setAllCombinations] = useState([]);
  // Active area tab in MiniFloorPlanPicker — used as preferred area when no
  // specific table is picked. Falls back to the first area in the picker.
  const [activeAreaId, setActiveAreaId] = useState(null);
  // Walk-in: intelligent best-table suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [comboSuggestions, setComboSuggestions] = useState([]);
  const [bestTableId, setBestTableId] = useState(null);
  const autoPickedRef = useRef(false);
  // Telefon-Suche: gefundener Gast (Name/E-Mail/Stammgast/Lieblingstisch)
  const [phoneMatch, setPhoneMatch] = useState(null);
  // Sperrliste: Eintrag, falls Telefon/E-Mail gesperrt sind
  const [blockedMatch, setBlockedMatch] = useState(null);
  // Vorbestell-Angebote (wie im Kundenformular, hier als kompakte Buttons)
  const [offers, setOffers] = useState([]);
  const [offerQty, setOfferQty] = useState({});  // offer_id -> quantity

  // Gast anhand der Telefonnummer suchen (debounced, ab 6 Ziffern).
  useEffect(() => {
    const digits = (form.phone || '').replace(/\D/g, '');
    if (digits.length < 6) { setPhoneMatch(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.get(`/guests/lookup?phone=${encodeURIComponent(form.phone)}`)
        .then(r => { if (alive) setPhoneMatch(r.data && r.data.found ? r.data : null); })
        .catch(() => { if (alive) setPhoneMatch(null); });
    }, 450);
    return () => { alive = false; clearTimeout(t); };
  }, [form.phone]);

  // Sperrliste prüfen (Telefon ODER E-Mail), debounced.
  useEffect(() => {
    const digits = (form.phone || '').replace(/\D/g, '');
    const email = (form.email || '').trim();
    if (digits.length < 5 && !email) { setBlockedMatch(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (form.phone) params.append('phone', form.phone);
      if (email) params.append('email', email);
      api.get(`/blocked-guests/check?${params.toString()}`)
        .then(r => { if (alive) setBlockedMatch(r.data && r.data.blocked ? r.data.entry : null); })
        .catch(() => { if (alive) setBlockedMatch(null); });
    }, 450);
    return () => { alive = false; clearTimeout(t); };
  }, [form.phone, form.email]);

  // Dauer automatisch anpassen:
  // - Service-Wellen: festes Wellen-Ende
  // - normale Tage: auf die Schließzeit (Ende des Öffnungsblocks) begrenzen
  useEffect(() => {
    const we = waveEndForDateTime(form.date, form.start_time, specialDays);
    if (we) {
      const dur = hmToMin(we) - hmToMin(form.start_time);
      if (dur > 0) setForm(f => (f.duration_minutes === dur ? f : { ...f, duration_minutes: dur }));
      return;
    }
    const maxDur = maxDurationForDateTime(form.date, form.start_time, openingHours, specialDays, settings);
    const walkinDur = Number(settings?.walkin_default_duration_minutes) || 0;
    const base = (walkIn && walkinDur > 0) ? walkinDur : durationForPersons(form.persons, settings);  // Walk-in: eigene Standarddauer, sonst je Personenzahl
    const target = (maxDur != null && maxDur > 0) ? Math.min(base, maxDur) : base;  // nie über die Schließzeit
    setForm(f => (f.duration_minutes === target ? f : { ...f, duration_minutes: target }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, form.start_time, form.persons, specialDays, openingHours, settings, walkIn]);

  // Voice-to-reservation (OpenAI Whisper transcription + GPT field extraction)
  const [voiceState, setVoiceState] = useState('idle');  // 'idle' | 'recording' | 'processing'
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);

  const stopMicStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
  };

  const startVoice = async () => {
    setVoiceTranscript('');
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Sprachaufnahme wird von diesem Browser nicht unterstützt.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => sendVoice();
      mediaRecorderRef.current = mr;
      mr.start();
      setVoiceState('recording');
    } catch {
      stopMicStream();
      toast.error('Mikrofon nicht verfügbar. Bitte Zugriff erlauben.');
      setVoiceState('idle');
    }
  };

  const stopVoice = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      setVoiceState('processing');
      mr.stop();  // triggers onstop -> sendVoice()
    } else {
      setVoiceState('idle');
    }
  };

  const sendVoice = async () => {
    stopMicStream();
    const chunks = audioChunksRef.current;
    const blob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
    if (!blob.size) { setVoiceState('idle'); toast.error('Keine Aufnahme erkannt.'); return; }
    const bt = blob.type || '';
    const ext = bt.includes('mp4') || bt.includes('m4a') ? 'mp4' : bt.includes('ogg') ? 'ogg' : 'webm';
    const fd = new FormData();
    fd.append('audio', blob, `aufnahme.${ext}`);
    try {
      const r = await api.post('/voice/parse-reservation', fd);
      const { transcript, fields } = r.data || {};
      setVoiceTranscript(transcript || '');
      if (fields && Object.keys(fields).length) {
        setForm(f => ({ ...f, ...fields }));
        const labels = { customer_name: 'Name', persons: 'Personen', date: 'Datum', start_time: 'Uhrzeit', note: 'Notiz' };
        toast.success(`Ausgefüllt: ${Object.keys(fields).map(k => labels[k] || k).join(', ')}`);
      } else {
        toast.warning(transcript ? 'Keine Felder erkannt – bitte manuell prüfen.' : 'Nichts verstanden – bitte erneut versuchen.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sprachverarbeitung fehlgeschlagen.');
    } finally {
      setVoiceState('idle');
    }
  };

  // Reset/cleanup voice recording when the dialog closes
  useEffect(() => {
    if (!open) {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch { /* noop */ } }
      stopMicStream();
      setVoiceState('idle');
      setVoiceTranscript('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get('/opening-hours/all'), api.get('/special-days'), api.get('/table-combinations/all')])
      .then(([a, b, c]) => { setOpeningHours(a.data); setSpecialDays(b.data); setAllCombinations(c.data || []); })
      .catch(() => {});
    api.get('/settings/public').then(r => setSettings(r.data || null)).catch(() => {});
    api.get('/offers').then(r => setOffers(r.data || [])).catch(() => setOffers([]));
  }, [open]);

  // Load reservations for the picked date to compute table availability
  useEffect(() => {
    if (!open || !form.date) { setDayReservations([]); return; }
    let cancelled = false;
    api.get(`/reservations?date=${form.date}`)
      .then(r => { if (!cancelled) setDayReservations(r.data || []); })
      .catch(() => setDayReservations([]));
    return () => { cancelled = true; };
  }, [open, form.date]);

  // Fetch availability for date/persons to mark "voll" slots
  useEffect(() => {
    if (!open || !form.date || !form.persons) { setAvailableTimes(null); return; }
    let cancelled = false;
    api.post('/availability', { date: form.date, persons: form.persons, duration_minutes: form.duration_minutes })
      .then(r => {
        if (cancelled) return;
        setAvailableTimes(new Set((r.data?.slots || []).map(s => s.time)));
      })
      .catch(() => setAvailableTimes(null));
    return () => { cancelled = true; };
  }, [open, form.date, form.persons, form.duration_minutes]);

  // Build a COMPLETELY FRESH form every time the dialog opens, so stale values
  // (name, persons, note, email, time …) from a previous entry never carry over.
  // Caller-ID (Anruferkennung) prefills phone + name; everything else is blank.
  useEffect(() => {
    if (!open) {
      setSuggestions([]); setComboSuggestions([]); setBestTableId(null);
      autoPickedRef.current = false;
      setPhoneMatch(null);
      setBlockedMatch(null);
      return;
    }
    const base = {
      customer_name: defaultName || (walkIn ? 'Walk-in' : ''),
      phone: defaultPhone || '',
      email: defaultEmail || '',
      date: defaultDate || '',
      start_time: defaultTime || '',
      persons: (defaultPersons != null && Number(defaultPersons) > 0) ? Number(defaultPersons) : 2,
      duration_minutes: 120,
      assigned_table_id: defaultTableId || '',
      note: '',
      internal_note: '',
      source: walkIn ? 'walk_in' : 'manual',
      auto_confirm: true,
    };
    if (walkIn) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(Math.floor(now.getMinutes() / 5) * 5).padStart(2, '0');
      base.start_time = `${hh}:${mm}`;
    }
    setForm(base);
    setPickedTableIds(defaultTableId ? [defaultTableId] : []);
    autoPickedRef.current = !!defaultTableId;  // don't auto-pick if a table was preselected
    setPhoneMatch(null);
    setBlockedMatch(null);
    setOfferQty({});
    setSuggestions([]); setComboSuggestions([]); setBestTableId(null);
  }, [open, defaultDate, defaultTableId, walkIn, defaultPhone, defaultName, defaultEmail, defaultPersons, defaultTime]);

  // Fetch best-table suggestions for the chosen party size / date / time.
  // Active for BOTH walk-ins (time = now) and manual reservations (chosen time).
  useEffect(() => {
    if (!open || !form.date || !form.start_time || !form.persons) {
      setSuggestions([]); setBestTableId(null); setComboSuggestions([]);
      return;
    }
    let cancelled = false;
    api.post('/walk-in/suggest', {
      date: form.date,
      start_time: form.start_time,
      persons: form.persons,
      duration_minutes: form.duration_minutes,
    }).then(r => {
      if (cancelled) return;
      const sugg = r.data?.suggestions || [];
      setSuggestions(sugg);
      setComboSuggestions(r.data?.combinations || []);
      setBestTableId(r.data?.best_table_id || null);
      // Auto-select the best table once — only for walk-ins (manual bookings let
      // the admin choose). Only when nothing is picked yet.
      if (walkIn && !autoPickedRef.current && r.data?.best_table_id && pickedTableIds.length === 0) {
        setPickedTableIds([r.data.best_table_id]);
        autoPickedRef.current = true;
      }
    }).catch(() => { if (!cancelled) { setSuggestions([]); setBestTableId(null); setComboSuggestions([]); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, walkIn, form.date, form.start_time, form.persons, form.duration_minutes]);

  const submit = async () => {
    if (!form.start_time || !form.date || !form.persons) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    try {
      const payload = { ...form };
      if (walkIn && shareTable) payload.share_table = true;
      // Multi-table pick → backend creates ad-hoc combo
      if (pickedTableIds.length >= 2) {
        payload.table_ids = pickedTableIds;
        payload.assigned_table_id = null;
        payload.area_id = null;  // explicit table pick wins over area filter
      } else if (pickedTableIds.length === 1) {
        payload.assigned_table_id = pickedTableIds[0];
        payload.table_ids = [];
        payload.area_id = null;
      } else {
        // No table picked → pass the currently active area tab so backend
        // restricts auto-assignment to that area (sorted by priority + smallest fit).
        payload.assigned_table_id = null;
        payload.table_ids = [];
        payload.area_id = activeAreaId || null;
      }
      payload.offers = Object.entries(offerQty)
        .filter(([, q]) => q > 0)
        .map(([id, q]) => ({ offer_id: id, quantity: q }));
      await api.post('/reservations/admin', payload);
      toast.success(walkIn ? 'Walk-in hinzugefügt' : 'Reservierung erstellt');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  // Offers available for the chosen party size (same rule as customer form)
  const availOffers = (offers || []).filter(o =>
    o.active !== false &&
    (form.persons || 0) >= (o.min_persons || 1) &&
    (o.max_persons == null || (form.persons || 0) <= o.max_persons)
  );
  const fmtPrice = (p) => `${(Number(p) || 0).toFixed(2).replace('.', ',')} €`;
  const offerTotal = availOffers.reduce((sum, o) => {
    const q = o.per_person ? ((offerQty[o.id] || 0) > 0 ? (form.persons || 0) : 0) : (offerQty[o.id] || 0);
    return sum + q * (Number(o.price) || 0);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {walkIn ? (shareTable ? 'Tisch teilen – weiterer Walk-in' : 'Walk-in hinzufügen') : 'Reservierung anlegen'}
          </DialogTitle>
        </DialogHeader>
        {VOICE_INPUT_ENABLED && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {voiceState === 'idle' && (
                <Button type="button" variant="outline" size="sm" onClick={startVoice} data-testid="voice-start-btn" className="gap-1.5 border-[#1C1C19]/20">
                  <Mic className="w-4 h-4" /> Per Sprache ausfüllen
                </Button>
              )}
              {voiceState === 'recording' && (
                <Button type="button" size="sm" onClick={stopVoice} data-testid="voice-stop-btn" className="gap-1.5 bg-[#EF4444] hover:bg-[#DC2626] text-white animate-pulse">
                  <Square className="w-3.5 h-3.5" fill="currentColor" /> Aufnahme stoppen
                </Button>
              )}
              {voiceState === 'processing' && (
                <Button type="button" size="sm" disabled data-testid="voice-processing-btn" className="gap-1.5 bg-[#1C1C19] text-white">
                  <Loader2 className="w-4 h-4 animate-spin" /> Wird verarbeitet…
                </Button>
              )}
              <span className="text-[11px] text-[#5C5C59]">z. B. „Tisch für 4 Personen morgen um 19 Uhr auf Müller, am Fenster"</span>
            </div>
            {voiceTranscript && (
              <div className="mb-2 text-xs text-[#5C5C59] bg-[#F9F9F7] border border-[#E5E5E0] rounded px-2 py-1.5" data-testid="voice-transcript">
                <span className="font-medium text-[#1C1C19]">Erkannt:</span> „{voiceTranscript}"
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} data-testid="new-name" /></div>
          <div><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="new-phone" /></div>
          <div><Label>E-Mail</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="new-email" /></div>
          {blockedMatch && (
            <div className="col-span-2 bg-[#FEF2F2] border border-[#DC2626] rounded-md px-3 py-2 flex items-start gap-2" data-testid="blocked-match-banner">
              <Ban className="w-4 h-4 text-[#DC2626] shrink-0 mt-0.5" />
              <div className="min-w-0 text-xs text-[#991B1B]">
                <span className="font-semibold">Gesperrter Gast</span>
                {blockedMatch.reason ? <span> — {blockedMatch.reason}</span> : null}
                <div className="text-[11px] text-[#B91C1C] mt-0.5">Dieser Gast steht auf der Sperrliste (Telefon/E-Mail). Online-Buchung ist blockiert.</div>
              </div>
            </div>
          )}
          {phoneMatch && (
            <div className="col-span-2 bg-[#F0FDF4] border border-[#86EFAC] rounded-md px-3 py-2 space-y-2" data-testid="phone-match-banner">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 text-xs flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-[#1C1C19]">{phoneMatch.name}</span>
                  {phoneMatch.is_regular && <span className="inline-flex items-center gap-1 text-[#B45309]"><Star className="w-3 h-3 fill-current" /> Stammgast</span>}
                  <span className="text-[#5C5C59]">{phoneMatch.visits} Besuch{phoneMatch.visits === 1 ? '' : 'e'}</span>
                  {phoneMatch.no_shows > 0 && <span className="text-[#EF4444]">· {phoneMatch.no_shows} No-Show</span>}
                </div>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, customer_name: phoneMatch.name || f.customer_name, email: phoneMatch.email || f.email }))}
                  data-testid="phone-match-apply"
                  className="shrink-0 text-xs px-2.5 py-1 rounded-md bg-[#1C1C19] text-white hover:bg-[#3A3A35] transition"
                >
                  Übernehmen
                </button>
              </div>
              <FavoriteTableEditor
                phone={form.phone}
                tables={tables}
                areas={areas}
                favoriteTableId={phoneMatch.favorite_table_id}
                favoriteTableNumber={phoneMatch.favorite_table_number}
                favoriteSource={phoneMatch.favorite_table_source}
                onChanged={(d) => setPhoneMatch(p => ({
                  ...p,
                  favorite_table_id: d.favorite_table_id,
                  favorite_table_number: d.favorite_table_number,
                  favorite_table_source: d.favorite_table_source,
                }))}
              />
            </div>
          )}
          <div><Label>Datum</Label><DatePickerField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="new-date" openingHours={openingHours} specialDays={specialDays} disablePast={!walkIn} settings={settings} /></div>
          <div><Label>Uhrzeit</Label><TimePickerField value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="new-start" dateStr={form.date} openingHours={openingHours} specialDays={specialDays} availableTimes={availableTimes} settings={settings} /></div>
          <div><Label>Personen</Label><Input type="number" min="1" value={form.persons} onChange={e => setForm({...form, persons: parseInt(e.target.value)})} data-testid="new-persons" /></div>
          <div className="col-span-2">
            <Label>Aufenthaltsdauer</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="new-duration-options">
              {(() => {
                const opts = [60, 75, 90, 105, 120, 150, 180];
                if (form.duration_minutes && !opts.includes(form.duration_minutes)) opts.push(form.duration_minutes);
                opts.sort((a, b) => a - b);
                return opts.map(min => {
                  const sel = form.duration_minutes === min;
                  return (
                    <button
                      key={min}
                      type="button"
                      onClick={() => setForm({ ...form, duration_minutes: min })}
                      data-testid={`new-duration-${min}`}
                      aria-pressed={sel}
                      className={`px-3 py-1.5 rounded-lg text-sm border transition ${sel ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'bg-white text-[#1C1C19] border-[#E5E5E0] hover:border-[#1C1C19]'}`}
                    >
                      {min} Min.
                    </button>
                  );
                });
              })()}
            </div>
            <p className="text-xs text-[#5C5C59] mt-1.5" data-testid="new-duration-selected">Ausgewählt: {form.duration_minutes} Min.</p>
          </div>
          <div className="col-span-2">
            {(suggestions.length > 0 || comboSuggestions.length > 0) && (
              <div className="mb-3 p-3 rounded-lg border border-[#22C55E]/40 bg-[#22C55E]/5" data-testid="walkin-suggestions">
                <div className="flex items-center gap-1.5 mb-2 text-[#15803D]">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase tracking-wider">
                    {walkIn ? 'Beste freie Tische jetzt' : `Beste freie Tische um ${form.start_time} Uhr`}
                  </span>
                </div>
                {suggestions.length > 0 && (
                <div className="space-y-3">
                  {Object.values(suggestions.reduce((acc, s) => {
                    const key = s.area_id || 'none';
                    if (!acc[key]) acc[key] = { area_name: s.area_name, items: [] };
                    if (acc[key].items.length < 3) acc[key].items.push(s);
                    return acc;
                  }, {})).map(group => (
                    <div key={group.area_name || 'none'} data-testid={`walkin-area-group-${group.area_name}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#15803D] mb-1.5">{group.area_name || 'Ohne Bereich'}</div>
                      <div className="flex flex-wrap gap-2">
                        {group.items.map(s => {
                          const picked = pickedTableIds.length === 1 && pickedTableIds[0] === s.table_id;
                          const isBest = s.table_id === bestTableId;
                          const hours = Math.floor(s.free_minutes / 60);
                          const mins = s.free_minutes % 60;
                          const freeLabel = `${hours > 0 ? `${hours} Std ` : ''}${mins > 0 ? `${mins} Min` : ''}`.trim();
                          return (
                            <button
                              key={s.table_id}
                              type="button"
                              onClick={() => setPickedTableIds([s.table_id])}
                              data-testid={`walkin-suggest-${s.table_id}`}
                              className={`text-left px-3 py-2 rounded-md border transition ${
                                picked ? 'border-[#22C55E] bg-[#22C55E] text-white' : 'border-[#E5E5E0] bg-white hover:border-[#22C55E]'
                              }`}
                            >
                              <div className="flex items-center gap-1.5">
                                {isBest && <Star className={`w-3.5 h-3.5 ${picked ? 'text-white' : 'text-[#EAB308]'}`} fill="currentColor" />}
                                <span className="font-display text-base leading-none">Tisch {s.table_number}</span>
                                {isBest && <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${picked ? 'bg-white/20' : 'bg-[#22C55E]/15 text-[#15803D]'}`}>Empfohlen</span>}
                              </div>
                              <div className={`text-[10px] mt-0.5 ${picked ? 'text-white/85' : 'text-[#5C5C59]'}`}>
                                {s.min_persons}–{s.max_persons} P. · frei {freeLabel}{s.free_until ? ` (bis ${s.free_until})` : ' (ganzer Abend)'}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                )}
                {comboSuggestions.length > 0 && (
                  <div className={suggestions.length > 0 ? "mt-3 pt-3 border-t border-[#22C55E]/30" : ""}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#15803D] mb-1.5">Tischgruppen / Kombinationen</div>
                    <div className="flex flex-wrap gap-2">
                      {comboSuggestions.map(c => {
                        const picked = pickedTableIds.length === c.table_ids.length && c.table_ids.every(id => pickedTableIds.includes(id));
                        const hours = Math.floor(c.free_minutes / 60);
                        const mins = c.free_minutes % 60;
                        const freeLabel = `${hours > 0 ? `${hours} Std ` : ''}${mins > 0 ? `${mins} Min` : ''}`.trim();
                        return (
                          <button
                            key={c.combination_id}
                            type="button"
                            onClick={() => setPickedTableIds(c.table_ids)}
                            data-testid={`walkin-combo-${c.combination_id}`}
                            className={`text-left px-3 py-2 rounded-md border transition ${picked ? 'border-[#22C55E] bg-[#22C55E] text-white' : 'border-[#E5E5E0] bg-white hover:border-[#22C55E]'}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5" />
                              <span className="font-display text-sm leading-none">{c.name}</span>
                            </div>
                            <div className={`text-[10px] mt-0.5 ${picked ? 'text-white/85' : 'text-[#5C5C59]'}`}>
                              Tische {c.table_numbers.join(' + ')} · {c.min_persons}–{c.max_persons} P. · {c.area_name} · frei {freeLabel}{c.free_until ? ` (bis ${c.free_until})` : ' (ganzer Abend)'}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {suggestions.length === 0 && comboSuggestions.length === 0 && form.date && form.start_time && form.persons && (
              <div className="mb-3 p-3 rounded-lg border border-[#F97316]/40 bg-[#F97316]/5 text-xs text-[#9A3412]" data-testid="walkin-no-suggestions">
                Aktuell kein freier Tisch für {form.persons} Pers. um {form.start_time}. Bitte Personen/Uhrzeit anpassen oder Tisch manuell wählen.
              </div>
            )}
            <Label>Tisch (optional – Mehrfach-Auswahl für Tischgruppe)</Label>
            <MiniFloorPlanPicker
              tables={tables}
              areas={areas}
              combinations={allCombinations}
              dayReservations={dayReservations}
              startTime={form.start_time}
              durationMinutes={form.duration_minutes}
              persons={form.persons}
              pickedTableIds={pickedTableIds}
              setPickedTableIds={setPickedTableIds}
              activeAreaId={activeAreaId}
              setActiveAreaId={setActiveAreaId}
              date={form.date}
            />
          </div>
          <div className="col-span-2"><Label>Bemerkung</Label><Textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} data-testid="new-note" /></div>
          <div className="col-span-2"><Label>Interne Notiz</Label><Textarea value={form.internal_note} onChange={e => setForm({...form, internal_note: e.target.value})} data-testid="new-internal" /></div>
          {availOffers.length > 0 && (
            <div className="col-span-2" data-testid="new-offer-section">
              <Label>Vorbestellung (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {availOffers.map(o => {
                  const qty = offerQty[o.id] || 0;
                  const selected = qty > 0;
                  if (o.per_person) {
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setOfferQty(prev => ({ ...prev, [o.id]: selected ? 0 : (form.persons || 1) }))}
                        data-testid={`new-offer-${o.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition flex items-center gap-1.5 ${selected ? 'border-[#B45309] bg-[#B45309] text-white' : 'border-[#E5E5E0] bg-white text-[#1C1C19] hover:border-[#B45309]'}`}
                      >
                        <Wine className="w-3.5 h-3.5" />
                        <span className="font-medium">{o.name}</span>
                        {Number(o.price) > 0 && <span className={selected ? 'text-white/80' : 'text-[#5C5C59]'}>{fmtPrice(o.price)} p.P.</span>}
                        {selected && <span className="ml-0.5 font-semibold">×{form.persons}</span>}
                      </button>
                    );
                  }
                  return (
                    <div
                      key={o.id}
                      data-testid={`new-offer-${o.id}`}
                      className={`px-2.5 py-1 rounded-full text-xs border flex items-center gap-1.5 ${selected ? 'border-[#B45309] bg-[#B45309]/10' : 'border-[#E5E5E0] bg-white'}`}
                    >
                      <Wine className="w-3.5 h-3.5 text-[#B45309]" />
                      <span className="font-medium text-[#1C1C19]">{o.name}</span>
                      {Number(o.price) > 0 && <span className="text-[#5C5C59]">{fmtPrice(o.price)}</span>}
                      <button
                        type="button"
                        onClick={() => setOfferQty(prev => ({ ...prev, [o.id]: Math.max(0, qty - 1) }))}
                        disabled={qty <= 0}
                        data-testid={`new-offer-minus-${o.id}`}
                        aria-label="Weniger"
                        className="w-5 h-5 rounded-full border border-[#E5E5E0] text-[#1C1C19] hover:border-[#B45309] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center leading-none"
                      >−</button>
                      <span data-testid={`new-offer-qty-${o.id}`} className="w-4 text-center font-mono tabular-nums">{qty}</span>
                      <button
                        type="button"
                        onClick={() => setOfferQty(prev => ({ ...prev, [o.id]: qty + 1 }))}
                        data-testid={`new-offer-plus-${o.id}`}
                        aria-label="Mehr"
                        className="w-5 h-5 rounded-full border border-[#E5E5E0] text-[#1C1C19] hover:border-[#B45309] flex items-center justify-center leading-none"
                      >+</button>
                    </div>
                  );
                })}
              </div>
              {offerTotal > 0 && (
                <div className="text-right text-xs text-[#5C5C59] mt-1.5" data-testid="new-offer-total">
                  Summe Vorbestellung: <span className="font-medium text-[#1C1C19]">{fmtPrice(offerTotal)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" data-testid="new-cancel-btn">Abbrechen</Button>
          <Button onClick={submit} className="bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="new-submit-btn">
            {walkIn ? 'Walk-in starten' : 'Erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// MiniFloorPlanPicker — admin-only multi-select grid of tables with live
// availability for the selected date + time + duration. Multiple selections
// create an ad-hoc table combination automatically on submit.
// Exported so other admin pages (e.g. AdminWaitlist) can reuse it.
// ---------------------------------------------------------------------------
export function MiniFloorPlanPicker({
  tables, areas, combinations, dayReservations,
  startTime, durationMinutes, persons,
  pickedTableIds, setPickedTableIds,
  excludeReservationId,
  date,
  activeAreaId: activeAreaIdProp, setActiveAreaId: setActiveAreaIdProp,
}) {
  const blocking = new Set(['pending', 'confirmed', 'arrived', 'seated']);
  const sMin = startTime ? hmToMin(startTime) : null;
  const eMin = sMin != null ? sMin + (durationMinutes || 120) : null;

  // Tagessperren für das gewählte Datum laden (synchron zum Tischplan).
  const [dayBlocks, setDayBlocks] = useState([]);
  useEffect(() => {
    if (!date) { setDayBlocks([]); return; }
    let cancelled = false;
    api.get(`/table-day-blocks?date=${date}`)
      .then(r => { if (!cancelled) setDayBlocks(r.data || []); })
      .catch(() => { if (!cancelled) setDayBlocks([]); });
    return () => { cancelled = true; };
  }, [date]);

  const blocksByTable = new Map();
  (dayBlocks || []).forEach(b => {
    if (!blocksByTable.has(b.table_id)) blocksByTable.set(b.table_id, []);
    blocksByTable.get(b.table_id).push(b);
  });

  // Build a map: table_id → set of combination_ids that include it
  const tableCombosMap = new Map();
  (combinations || []).forEach(c => {
    (c.table_ids || []).forEach(tid => {
      if (!tableCombosMap.has(tid)) tableCombosMap.set(tid, new Set());
      tableCombosMap.get(tid).add(c.id);
    });
  });

  // Determine if a table is busy in the selected window
  function tableStatus(t) {
    if (!t.active) return { busy: true, reason: 'inactive' };
    if (t.status_override === 'blocked') return { busy: true, reason: 'blocked' };
    // Tagessperre: ganztags blockiert immer, Zeitfenster nur bei Überschneidung.
    const tBlocks = blocksByTable.get(t.id) || [];
    for (const b of tBlocks) {
      const fullDay = b.full_day || !b.start_time || !b.end_time;
      if (fullDay) return { busy: true, reason: 'blocked', block: b };
      if (sMin != null && eMin != null) {
        const bs = hmToMin(b.start_time);
        const be = hmToMin(b.end_time);
        if (bs < eMin && be > sMin) return { busy: true, reason: 'blocked', block: b };
      }
    }
    if (sMin == null || eMin == null) return { busy: false, reason: null };
    const comboIds = tableCombosMap.get(t.id) || new Set();
    for (const r of dayReservations) {
      if (excludeReservationId && r.id === excludeReservationId) continue;
      if (!blocking.has(r.status)) continue;
      const occupies =
        r.assigned_table_id === t.id ||
        (r.assigned_combination_id && comboIds.has(r.assigned_combination_id));
      if (!occupies) continue;
      const rs = hmToMin(r.start_time);
      const re = hmToMin(r.end_time);
      if (rs < eMin && re > sMin) return { busy: true, reason: 'reserved', resv: r };
    }
    return { busy: false, reason: null };
  }

  // All blocking reservations of the selected day per table (regardless of time slot).
  // Used to show a small indicator/badge even before a time is picked.
  function reservationsForTable(t) {
    const comboIds = tableCombosMap.get(t.id) || new Set();
    return dayReservations
      .filter(r => {
        if (excludeReservationId && r.id === excludeReservationId) return false;
        if (!blocking.has(r.status)) return false;
        return r.assigned_table_id === t.id ||
          (r.assigned_combination_id && comboIds.has(r.assigned_combination_id));
      })
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  }

  const toggle = (tid, busy) => {
    if (busy) return;
    setPickedTableIds(prev =>
      prev.includes(tid) ? prev.filter(x => x !== tid) : [...prev, tid]
    );
  };

  const totalCap = pickedTableIds.reduce((acc, tid) => {
    const t = tables.find(x => x.id === tid);
    return acc + (t?.max_persons || 0);
  }, 0);

  const tablesByArea = (areas || []).map(a => ({
    area: a,
    list: tables.filter(t => t.area_id === a.id),
  })).filter(g => g.list.length > 0);

  // Active area tab (default: first area). Lifted to parent so submit can read it.
  // Falls back to internal state if parent doesn't provide one.
  const [internalActiveAreaId, setInternalActiveAreaId] = useState(null);
  const activeAreaId = activeAreaIdProp !== undefined ? activeAreaIdProp : internalActiveAreaId;
  const setActiveAreaId = setActiveAreaIdProp || setInternalActiveAreaId;

  const activeGroup =
    tablesByArea.find(g => g.area.id === activeAreaId) ||
    tablesByArea[0] ||
    null;

  // Count picked tables per area (so admin sees badges on other tabs)
  const pickedCountByArea = new Map();
  pickedTableIds.forEach(tid => {
    const t = tables.find(x => x.id === tid);
    if (!t) return;
    pickedCountByArea.set(t.area_id, (pickedCountByArea.get(t.area_id) || 0) + 1);
  });

  return (
    <div data-testid="mini-floor-plan-picker" className="border border-[#E5E5E0] rounded p-2 bg-[#F9F9F7]">
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-xs text-[#5C5C59]">
          {pickedTableIds.length === 0
            ? 'Kein Tisch gewählt → Automatik'
            : pickedTableIds.length === 1
              ? `1 Tisch · ${totalCap} Plätze`
              : `${pickedTableIds.length} Tische (Gruppe) · ${totalCap} Plätze`}
          {sMin == null && <span className="ml-2 text-[#EAB308]">⚠ Uhrzeit wählen für Verfügbarkeit</span>}
        </div>
        {pickedTableIds.length > 0 && (
          <button
            type="button"
            onClick={() => setPickedTableIds([])}
            className="text-[10px] uppercase tracking-wider text-[#5C5C59] hover:text-[#1C1C19]"
            data-testid="mini-fp-reset"
          >
            Auswahl zurücksetzen
          </button>
        )}
      </div>

      {/* Area tabs */}
      {tablesByArea.length > 1 && (
        <div
          className="flex gap-1 mb-2 overflow-x-auto pb-1 -mx-0.5 px-0.5"
          data-testid="mini-fp-area-tabs"
        >
          {tablesByArea.map(({ area }) => {
            const isActive = area.id === (activeGroup?.area.id);
            const pickedHere = pickedCountByArea.get(area.id) || 0;
            return (
              <button
                key={area.id}
                type="button"
                onClick={() => setActiveAreaId(area.id)}
                data-testid={`mini-fp-area-tab-${area.id}`}
                data-active={isActive ? 'true' : 'false'}
                className={`relative shrink-0 px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-medium border transition ${
                  isActive
                    ? 'bg-[#1C1C19] text-white border-[#1C1C19]'
                    : 'bg-white text-[#5C5C59] border-[#E5E5E0] hover:border-[#1C1C19]'
                }`}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                  style={{ background: area.color || '#5C5C59' }}
                ></span>
                {area.name}
                {pickedHere > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#EAB308] text-[#1C1C19] text-[9px] font-bold align-middle"
                    data-testid={`mini-fp-area-tab-badge-${area.id}`}
                  >
                    {pickedHere}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="max-h-64 overflow-y-auto">
        {activeGroup && (
          <div key={activeGroup.area.id} data-testid={`mini-fp-area-${activeGroup.area.id}`}>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-1.5">
              {activeGroup.list.map(t => {
                const st = tableStatus(t);
                const picked = pickedTableIds.includes(t.id);
                const dayResvs = reservationsForTable(t);
                const hasDayResvs = dayResvs.length > 0;
                const dayResvList = dayResvs
                  .map(r => `${r.start_time}-${r.end_time} ${r.customer_name || ''}`)
                  .join('\n');
                const blkInfo = st.block
                  ? (st.block.full_day || !st.block.start_time || !st.block.end_time
                      ? 'Ganztags gesperrt'
                      : `Gesperrt ${st.block.start_time}–${st.block.end_time}`)
                    + (st.block.note ? ` · ${st.block.note}` : '')
                  : 'Tisch gesperrt';
                const tip = st.reason === 'blocked' ? blkInfo
                          : st.reason === 'inactive' ? 'Tisch inaktiv'
                          : st.reason === 'reserved' ? `Belegt: ${st.resv?.customer_name || ''} ${st.resv?.start_time || ''}-${st.resv?.end_time || ''}`
                          : hasDayResvs
                            ? `Tisch ${t.table_number} · ${t.min_persons}-${t.max_persons} P.\n--- Reservierungen heute ---\n${dayResvList}`
                            : `Tisch ${t.table_number} · ${t.min_persons}-${t.max_persons} P.`;
                const bg = st.busy ? '#F3F4F6' : picked ? '#1C1C19' : '#FFFFFF';
                const fg = st.busy ? '#9CA3AF' : picked ? '#FFFFFF' : '#1C1C19';
                const border = st.busy ? '#E5E5E0' : picked ? '#1C1C19' : (activeGroup.area.color || '#1C1C19');
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id, st.busy)}
                    disabled={st.busy}
                    title={tip}
                    data-testid={`mini-fp-table-${t.id}`}
                    data-picked={picked ? 'true' : 'false'}
                    data-busy={st.busy ? 'true' : 'false'}
                    data-has-day-resvs={hasDayResvs ? 'true' : 'false'}
                    className={`relative px-2 py-1.5 rounded text-xs font-mono border transition ${st.busy ? 'cursor-not-allowed line-through' : 'hover:scale-105'}`}
                    style={{ background: bg, color: fg, borderColor: border }}
                  >
                    <div className="font-medium">{t.table_number}</div>
                    {st.reason === 'reserved' && st.resv?.start_time ? (
                      <div className="text-[9px] font-semibold leading-none" style={{ textDecoration: 'none', color: '#EF4444' }} data-testid={`mini-fp-table-time-${t.id}`}>
                        ab {st.resv.start_time}
                      </div>
                    ) : (
                      <div className="text-[9px] opacity-70">{t.min_persons}-{t.max_persons}P</div>
                    )}
                    {st.reason === 'reserved' && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#EF4444]"></span>
                    )}
                    {/* Day-load indicator: show small badge with reservation count
                        when the table has bookings today but is not busy in the picked slot. */}
                    {!st.busy && hasDayResvs && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EAB308] text-[#1C1C19] text-[9px] font-bold inline-flex items-center justify-center"
                        data-testid={`mini-fp-table-day-count-${t.id}`}
                      >
                        {dayResvs.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 px-1 text-[10px] text-[#5C5C59]">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white border border-[#1C1C19]"></span>Frei</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#1C1C19]"></span>Ausgewählt</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EF4444]"></span>Belegt/Gesperrt</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EAB308]"></span>Reservierungen am Tag (Anzahl)</span>
        {persons && pickedTableIds.length > 0 && totalCap < persons && (
          <span className="ml-auto text-[#EAB308]">⚠ Kapazität ({totalCap}) &lt; Personen ({persons})</span>
        )}
      </div>
    </div>
  );
}
